import { useCallback, useEffect, useState } from 'react';

import { useWindowSize } from './useWindowSize';

/**
 * A custom hook returning a value based on current window width.
 *
 * Example Usage:
 * const [ myCustomValue ] = useValueBasedOnWindowSize({
 *      0: 1,
 *      600: 2,
 *      900: 3,
 * }]
 *
 * If screen width is 700, `myCustomValue` would be `2`.
 *
 * @param config - Configuration object consisting of breakpoint - value pair. Always provide value for 0 width!
 */
export const useValueBasedOnWindowSize = <TReturnValue>(
    config: Record<number, TReturnValue>
): TReturnValue[] => {
    const { width } = useWindowSize();

    const findCurrentValue = useCallback((): TReturnValue => {
        const key = Object.keys(config)
            .reverse()
            .find((breakPoint) => Number(breakPoint) < width);

        return config[Number(key)];
    }, [config, width]);

    const [currentValue, setCurrentValue] = useState(findCurrentValue());

    useEffect(() => {
        setCurrentValue(findCurrentValue());
    }, [findCurrentValue]);

    return [currentValue];
};
